import React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';

interface MultiSelectListboxProps<T> {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: T[];
  getOptionValue: (option: T) => string;
  /** Renders the option's label; `selected` lets callers bold the current choice. */
  renderOption: (option: T, selected: boolean) => React.ReactNode;
  /** Text shown in the trigger button summarizing the current selection. */
  renderSummary: (selected: string[]) => React.ReactNode;
}

/**
 * Generic multi-select dropdown built on Headless UI's Listbox. Used by the recipe
 * filters (item code / roast type / machine) which share identical chrome but
 * differ only in option shape and summary text.
 */
export function MultiSelectListbox<T>({
  label,
  value,
  onChange,
  options,
  getOptionValue,
  renderOption,
  renderSummary,
}: MultiSelectListboxProps<T>) {
  return (
    <div className="w-full sm:w-56">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <Listbox value={value} onChange={onChange} multiple>
        <div className="relative mt-1">
          <ListboxButton className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]">
            <span className="block truncate">{renderSummary(value)}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {options.map((option) => (
              <ListboxOption
                key={getOptionValue(option)}
                value={getOptionValue(option)}
                className={({ focus }) =>
                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                }
              >
                {({ selected }) => (
                  <>
                    {renderOption(option, selected)}
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  );
}
